'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TireSizeCalculator() {
  const t = useTranslations("tools.tireSizeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'single' | 'compare'>('single');

  // Single tire calculator state
  const [singleInputs, setSingleInputs] = useState({
    width: 265,
    aspect: 70,
    wheel: 17,
    newWheelEnabled: false,
    newWheel: 19
  });
  const [singleResults, setSingleResults] = useState<any>(null);
  const [alternatives, setAlternatives] = useState<any[]>([]);

  // Compare tires state
  const [compareInputs, setCompareInputs] = useState({
    width1: 265,
    aspect1: 70,
    wheel1: 17,
    width2: 265,
    aspect2: 40,
    wheel2: 23
  });
  const [compareResults, setCompareResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const calculateTire = (width: number, aspect: number, wheel: number) => {
    const widthMM = parseFloat(width.toString());
    const aspectRatio = parseFloat(aspect.toString()) / 100;
    const wheelInches = parseFloat(wheel.toString());

    const sidewallMM = widthMM * aspectRatio;
    const sidewallInches = sidewallMM / 25.4;
    const diameterInches = wheelInches + (2 * sidewallInches);
    const diameterMM = diameterInches * 25.4;
    const circumferenceInches = Math.PI * diameterInches;
    const circumferenceMM = Math.PI * diameterMM;
    const revolutionsPerMile = 63360 / circumferenceInches;

    return {
      widthMM,
      widthInches: widthMM / 25.4,
      sidewallMM,
      sidewallInches,
      diameterInches,
      diameterMM,
      circumferenceInches,
      circumferenceMM,
      revolutionsPerMile
    };
  };

  const findAlternativeTires = (originalDiameter: number, newWheel: number) => {
    const alternatives = [];
    const targetDiameter = originalDiameter;
    const newWheelInches = parseFloat(newWheel.toString());

    for (let width = 185; width <= 335; width += 10) {
      for (let aspect = 30; aspect <= 80; aspect += 5) {
        const tire = calculateTire(width, aspect, newWheelInches);
        const diff = Math.abs(tire.diameterInches - targetDiameter);
        const diffPercent = (diff / targetDiameter) * 100;

        if (diffPercent <= 3) {
          alternatives.push({
            size: `${width}/${aspect}R${newWheelInches}`,
            diameter: tire.diameterInches,
            diffPercent,
            sidewall: tire.sidewallInches
          });
        }
      }
    }

    return alternatives.sort((a, b) => a.diffPercent - b.diffPercent).slice(0, 10);
  };

  const handleSingleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const tire = calculateTire(singleInputs.width, singleInputs.aspect, singleInputs.wheel);
    setSingleResults(tire);

    if (singleInputs.newWheelEnabled && singleInputs.newWheel) {
      const alts = findAlternativeTires(tire.diameterInches, singleInputs.newWheel);
      setAlternatives(alts);
    } else {
      setAlternatives([]);
    }
  };

  const handleCompareCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const tire1 = calculateTire(compareInputs.width1, compareInputs.aspect1, compareInputs.wheel1);
    const tire2 = calculateTire(compareInputs.width2, compareInputs.aspect2, compareInputs.wheel2);

    const diameterDiff = tire2.diameterInches - tire1.diameterInches;
    const diameterDiffPercent = (diameterDiff / tire1.diameterInches) * 100;
    const widthDiff = tire2.widthMM - tire1.widthMM;
    const circDiff = tire2.circumferenceInches - tire1.circumferenceInches;
    const speedoDiff = diameterDiffPercent;

    setCompareResults({
      tire1,
      tire2,
      diameterDiff,
      diameterDiffPercent,
      widthDiff,
      circDiff,
      speedoDiff
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '800px' },
    { maxWidth: '900px' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const tabButtonStyle = (isActive: boolean) => getResponsiveStyle(
    {
      background: isActive ? '#2563eb' : 'white',
      color: isActive ? 'white' : '#64748b',
      border: `2px solid ${isActive ? '#2563eb' : '#e2e8f0'}`,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease'
    },
    { padding: '0.75rem 1rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1.25rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const tireInputGroupStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap' as const,
      marginBottom: '1.5rem'
    },
    { gap: '0.25rem' },
    { gap: '0.5rem' },
    { gap: '0.5rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      transition: 'border-color 0.3s ease'
    },
    { width: '60px', padding: '0.5rem', fontSize: '1rem' },
    { width: '70px', padding: '0.625rem', fontSize: '1.125rem' },
    { width: '80px', padding: '0.75rem', fontSize: '1.125rem' }
  );

  const separatorStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#64748b' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginBottom: '2rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultCardStyle = {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #2563eb'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const visualComparisonStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'flex-end',
      margin: '2rem 0',
      padding: '2rem',
      background: '#f8fafc',
      borderRadius: '0.75rem',
      minHeight: '300px'
    },
    { flexDirection: 'column' as const, gap: '2rem', alignItems: 'center' },
    { flexDirection: 'row' as const, gap: '1rem' },
    { flexDirection: 'row' as const }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={{ textAlign: 'center' as const, marginBottom: '2rem' }}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={summaryTextStyle}>{t("toolDescription")}</p>
          </div>

          <div style={tabsContainerStyle}>
            <button
              style={tabButtonStyle(activeTab === 'single')}
              onClick={() => setActiveTab('single')}
            >
              {t("singleTabButton")}
            </button>
            <button
              style={tabButtonStyle(activeTab === 'compare')}
              onClick={() => setActiveTab('compare')}
            >
              {t("compareTabButton")}
            </button>
          </div>

          {activeTab === 'single' && (
            <div style={calculatorCardStyle}>
              <form onSubmit={handleSingleCalculate}>
                <div style={tireInputGroupStyle}>
                  <label style={{ fontWeight: 600, color: colors.textPrimary }}>{t("tireSizeLabel")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="265"
                    min="100"
                    max="400"
                    value={singleInputs.width}
                    onChange={(e) => setSingleInputs({...singleInputs, width: parseInt(e.target.value) || 0})}
                    required
                  />
                  <span style={separatorStyle}>/</span>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="70"
                    min="25"
                    max="85"
                    value={singleInputs.aspect}
                    onChange={(e) => setSingleInputs({...singleInputs, aspect: parseInt(e.target.value) || 0})}
                    required
                  />
                  <span style={separatorStyle}>R</span>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="17"
                    min="10"
                    max="30"
                    value={singleInputs.wheel}
                    onChange={(e) => setSingleInputs({...singleInputs, wheel: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary }}>
                    {t("newWheelQuestion")}
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                      <input
                        type="radio"
                        name="newWheel"
                        checked={!singleInputs.newWheelEnabled}
                        onChange={() => setSingleInputs({...singleInputs, newWheelEnabled: false})}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span>{t("no")}</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                      <input
                        type="radio"
                        name="newWheel"
                        checked={singleInputs.newWheelEnabled}
                        onChange={() => setSingleInputs({...singleInputs, newWheelEnabled: true})}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span>{t("yes")}</span>
                    </label>
                  </div>
                  {singleInputs.newWheelEnabled && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem' }}>
                      <label style={{ fontWeight: 600, color: colors.textPrimary }}>
                        {t("newWheelDiameterLabel")}
                      </label>
                      <input
                        type="number"
                        style={{ ...inputStyle, width: '100px', marginLeft: '0.5rem' }}
                        placeholder="19"
                        min="10"
                        max="30"
                        value={singleInputs.newWheel}
                        onChange={(e) => setSingleInputs({...singleInputs, newWheel: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  )}
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t("calculateButton")}
                </button>
              </form>

              {singleResults && (
                <div style={{ marginTop: '2rem' }}>
                  <h3 style={{ ...h2Style, textAlign: 'center' as const }}>{t("specificationsTitle")}</h3>
                  <div style={resultsGridStyle}>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("sectionWidth")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {singleResults.widthMM.toFixed(0)} mm ({singleResults.widthInches.toFixed(2)} in)
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("sidewallHeight")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {singleResults.sidewallMM.toFixed(1)} mm ({singleResults.sidewallInches.toFixed(2)} in)
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("overallDiameter")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {singleResults.diameterMM.toFixed(1)} mm ({singleResults.diameterInches.toFixed(2)} in)
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("circumference")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {singleResults.circumferenceMM.toFixed(1)} mm ({singleResults.circumferenceInches.toFixed(2)} in)
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("revolutionsPerMile")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {singleResults.revolutionsPerMile.toFixed(0)}
                      </span>
                    </div>
                  </div>

                  {alternatives.length > 0 && (
                    <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#fef3c7', borderRadius: '0.75rem', borderLeft: '4px solid #f59e0b' }}>
                      <h4 style={{ color: '#92400e', marginBottom: '1rem' }}>{t("alternativeSizesTitle")}</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("tireSize")}</th>
                              <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("diameter")}</th>
                              <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("difference")}</th>
                              <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("sidewall")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {alternatives.map((alt, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '1rem' }}><strong>{alt.size}</strong></td>
                                <td style={{ padding: '1rem' }}>{alt.diameter.toFixed(2)} in</td>
                                <td style={{ padding: '1rem', color: alt.diffPercent < 1 ? '#64748b' : '#059669', fontWeight: 600 }}>
                                  {alt.diffPercent.toFixed(2)}%
                                </td>
                                <td style={{ padding: '1rem' }}>{alt.sidewall.toFixed(2)} in</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'compare' && (
            <div style={calculatorCardStyle}>
              <form onSubmit={handleCompareCalculate}>
                <div style={tireInputGroupStyle}>
                  <label style={{ fontWeight: 600, color: colors.textPrimary }}>{t("tire1Label")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="265"
                    min="100"
                    max="400"
                    value={compareInputs.width1}
                    onChange={(e) => setCompareInputs({...compareInputs, width1: parseInt(e.target.value) || 0})}
                    required
                  />
                  <span style={separatorStyle}>/</span>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="70"
                    min="25"
                    max="85"
                    value={compareInputs.aspect1}
                    onChange={(e) => setCompareInputs({...compareInputs, aspect1: parseInt(e.target.value) || 0})}
                    required
                  />
                  <span style={separatorStyle}>R</span>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="17"
                    min="10"
                    max="30"
                    value={compareInputs.wheel1}
                    onChange={(e) => setCompareInputs({...compareInputs, wheel1: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>

                <div style={tireInputGroupStyle}>
                  <label style={{ fontWeight: 600, color: colors.textPrimary }}>{t("tire2Label")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="265"
                    min="100"
                    max="400"
                    value={compareInputs.width2}
                    onChange={(e) => setCompareInputs({...compareInputs, width2: parseInt(e.target.value) || 0})}
                    required
                  />
                  <span style={separatorStyle}>/</span>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="40"
                    min="25"
                    max="85"
                    value={compareInputs.aspect2}
                    onChange={(e) => setCompareInputs({...compareInputs, aspect2: parseInt(e.target.value) || 0})}
                    required
                  />
                  <span style={separatorStyle}>R</span>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="23"
                    min="10"
                    max="30"
                    value={compareInputs.wheel2}
                    onChange={(e) => setCompareInputs({...compareInputs, wheel2: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t("compareTiresButton")}
                </button>
              </form>

              {compareResults && (
                <div style={{ marginTop: '2rem' }}>
                  <h3 style={{ ...h2Style, textAlign: 'center' as const }}>{t("comparisonTitle")}</h3>

                  <div style={visualComparisonStyle}>
                    <div style={{ textAlign: 'center' as const }}>
                      <div style={{
                        width: `${Math.min(compareResults.tire1.diameterInches * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches)), 200)}px`,
                        height: `${Math.min(compareResults.tire1.diameterInches * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches)), 200)}px`,
                        margin: '0 auto 1rem',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle at 30% 30%, #475569, #1e293b)',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: `${(compareResults.tire1.diameterInches - 2 * compareResults.tire1.sidewallInches) * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches))}px`,
                          height: `${(compareResults.tire1.diameterInches - 2 * compareResults.tire1.sidewallInches) * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches))}px`,
                          background: '#cbd5e1',
                          borderRadius: '50%',
                          boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.3)'
                        }}></div>
                      </div>
                      <div style={{ fontWeight: 600, color: colors.textPrimary, marginTop: '0.5rem' }}>
                        {t("tire1Label")}: {compareInputs.width1}/{compareInputs.aspect1}R{compareInputs.wheel1}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        {compareResults.tire1.diameterInches.toFixed(2)}" {t("diameter")}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' as const }}>
                      <div style={{
                        width: `${Math.min(compareResults.tire2.diameterInches * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches)), 200)}px`,
                        height: `${Math.min(compareResults.tire2.diameterInches * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches)), 200)}px`,
                        margin: '0 auto 1rem',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle at 30% 30%, #475569, #1e293b)',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: `${(compareResults.tire2.diameterInches - 2 * compareResults.tire2.sidewallInches) * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches))}px`,
                          height: `${(compareResults.tire2.diameterInches - 2 * compareResults.tire2.sidewallInches) * (200 / Math.max(compareResults.tire1.diameterInches, compareResults.tire2.diameterInches))}px`,
                          background: '#cbd5e1',
                          borderRadius: '50%',
                          boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.3)'
                        }}></div>
                      </div>
                      <div style={{ fontWeight: 600, color: colors.textPrimary, marginTop: '0.5rem' }}>
                        {t("tire2Label")}: {compareInputs.width2}/{compareInputs.aspect2}R{compareInputs.wheel2}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        {compareResults.tire2.diameterInches.toFixed(2)}" {t("diameter")}
                      </div>
                    </div>
                  </div>

                  <div style={resultsGridStyle}>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("diameterDifference")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {Math.abs(compareResults.diameterDiff).toFixed(2)} in{' '}
                        <span style={{ color: compareResults.diameterDiff >= 0 ? '#059669' : '#dc2626', fontSize: '1rem' }}>
                          ({compareResults.diameterDiffPercent >= 0 ? '+' : ''}{compareResults.diameterDiffPercent.toFixed(2)}%)
                        </span>
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("widthDifference")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {Math.abs(compareResults.widthDiff).toFixed(0)} mm{' '}
                        <span style={{ color: compareResults.widthDiff >= 0 ? '#059669' : '#dc2626', fontSize: '1rem' }}>
                          ({compareResults.widthDiff >= 0 ? '+' : ''}{compareResults.widthDiff.toFixed(0)} mm)
                        </span>
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("circumferenceDifference")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: colors.textPrimary, fontWeight: 700 }}>
                        {Math.abs(compareResults.circDiff).toFixed(2)} in{' '}
                        <span style={{ color: compareResults.circDiff >= 0 ? '#059669' : '#dc2626', fontSize: '1rem' }}>
                          ({compareResults.circDiff >= 0 ? '+' : ''}{compareResults.circDiff.toFixed(2)} in)
                        </span>
                      </span>
                    </div>
                    <div style={resultCardStyle}>
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {t("speedometerDifference")}
                      </span>
                      <span style={{ display: 'block', fontSize: '1.5rem', color: compareResults.speedoDiff >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>
                        {compareResults.speedoDiff >= 0 ? '+' : ''}{compareResults.speedoDiff.toFixed(2)}%
                        <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '0.25rem', color: '#64748b' }}>
                          {t("at60mph")}: {(60 * (1 + compareResults.speedoDiff/100)).toFixed(1)} mph {t("actual")}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                      <thead>
                        <tr>
                          <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("specification")}</th>
                          <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("tire1Label")}</th>
                          <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("tire2Label")}</th>
                          <th style={{ background: '#2563eb', color: 'white', padding: '1rem', textAlign: 'left' }}>{t("difference")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '1rem' }}><strong>{t("width")}</strong></td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire1.widthMM.toFixed(0)} mm</td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire2.widthMM.toFixed(0)} mm</td>
                          <td style={{ padding: '1rem', color: compareResults.widthDiff >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {compareResults.widthDiff >= 0 ? '+' : ''}{compareResults.widthDiff.toFixed(0)} mm
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '1rem' }}><strong>{t("sidewallHeight")}</strong></td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire1.sidewallMM.toFixed(1)} mm</td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire2.sidewallMM.toFixed(1)} mm</td>
                          <td style={{ padding: '1rem', color: (compareResults.tire2.sidewallMM - compareResults.tire1.sidewallMM) >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {(compareResults.tire2.sidewallMM - compareResults.tire1.sidewallMM) >= 0 ? '+' : ''}{(compareResults.tire2.sidewallMM - compareResults.tire1.sidewallMM).toFixed(1)} mm
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '1rem' }}><strong>{t("overallDiameter")}</strong></td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire1.diameterInches.toFixed(2)} in</td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire2.diameterInches.toFixed(2)} in</td>
                          <td style={{ padding: '1rem', color: compareResults.diameterDiff >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {compareResults.diameterDiff >= 0 ? '+' : ''}{compareResults.diameterDiff.toFixed(2)} in
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '1rem' }}><strong>{t("circumference")}</strong></td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire1.circumferenceInches.toFixed(2)} in</td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire2.circumferenceInches.toFixed(2)} in</td>
                          <td style={{ padding: '1rem', color: compareResults.circDiff >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {compareResults.circDiff >= 0 ? '+' : ''}{compareResults.circDiff.toFixed(2)} in
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '1rem' }}><strong>{t("revolutionsPerMile")}</strong></td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire1.revolutionsPerMile.toFixed(0)}</td>
                          <td style={{ padding: '1rem' }}>{compareResults.tire2.revolutionsPerMile.toFixed(0)}</td>
                          <td style={{ padding: '1rem', color: (compareResults.tire2.revolutionsPerMile - compareResults.tire1.revolutionsPerMile) >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {(compareResults.tire2.revolutionsPerMile - compareResults.tire1.revolutionsPerMile) >= 0 ? '+' : ''}{(compareResults.tire2.revolutionsPerMile - compareResults.tire1.revolutionsPerMile).toFixed(0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>{t("howToStep1")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>{t("howToStep2")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>{t("howToStep3")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>{t("howToStep4")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("insightSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("insightParagraph1")}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("insightSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("insightParagraph2")}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("insightSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("insightParagraph3")}</p>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>{t("insightBullet1")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>{t("insightBullet2")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>{t("insightBullet3")}</li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>{t("insightBullet4")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailParagraph1")}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailParagraph2")}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailParagraph3")}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.125rem', marginBottom: '0.5rem' }}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.125rem', marginBottom: '0.5rem' }}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.125rem', marginBottom: '0.5rem' }}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.125rem', marginBottom: '0.5rem' }}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.125rem', marginBottom: '0.5rem' }}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>
            <a href="https://trailtire.com/blog/finding-the-right-tire-size/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Finding the Right Tire Size - Trail Tire Auto Centers
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>
            <a href="https://www.lesschwab.com/article/tires/tire-size-calculator-what-size-tires-should-you-get.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Tire Size Calculator: What Size Tires Should You Get? - Les Schwab
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>
            <a href="https://www.performanceplustire.com/Blog/what-is-the-3-percent-rule-for-tires" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              What is the 3% Rule for Tires? Expert Guide to Safe Tire Sizing - Performance Plus Tire
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>
            <a href="https://www.bridgestoneamericas.com/en/company/safety/choosing-tires/determining-tire-size" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              How to Read & Determine Tire Size for Your Vehicle - Bridgestone
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.5rem' }}>
            <a href="https://www.lesschwab.com/article/tires/tire-size-explained-reading-the-sidewall.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Tire Size Explained: What the Numbers Mean - Les Schwab
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}